import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  createFormatInput,
  discipline as disciplineEnum,
  registrationMode as registrationModeEnum,
  formatStatus,
  type AdminFormat,
  type CreateFormatInput,
  type UpdateFormatInput,
} from '@gg/shared';
import {
  Button,
  Card,
  CheckboxField,
  ConfirmDialog,
  SelectField,
  TextAreaField,
  TextField,
} from '@/components/ui';
import { adminApiErrorKey, adminIssueKey } from '@/lib/adminErrors';
import { eventLocalToIso, isoToEventLocal } from '@/lib/dates';
import { ApiRequestError } from '@/lib/api';
import { useAdminFormats, useCreateFormat, useDeleteFormat, useUpdateFormat } from '@/lib/queries';
import { AdminTiers } from './AdminTiers';

type Values = {
  slug: string;
  name: string;
  nameEn: string;
  description: string;
  descriptionEn: string;
  discipline: CreateFormatInput['discipline'];
  distanceKm: string;
  elevationM: string;
  startsAt: string;
  capacity: string;
  requiresMedicalDocument: boolean;
  brevoListId: string;
  sortOrder: string;
  status: CreateFormatInput['status'];
  registrationMode: CreateFormatInput['registrationMode'];
  teamSizeMin: string;
  teamSizeMax: string;
};
type Field = keyof CreateFormatInput;

/** "" → null, digits → number, anything else → NaN (refused by the shared schema). */
const intOrNull = (s: string) =>
  s.trim() === '' ? null : /^\d+$/.test(s.trim()) ? Number(s) : NaN;
const decimalOrNull = (s: string) => {
  const v = s.trim().replace(',', '.');
  return v === '' ? null : /^\d+(\.\d)?$/.test(v) ? Number(v) : NaN;
};

const toValues = (f: AdminFormat | null, nextSort: number): Values => ({
  slug: f?.slug ?? '',
  name: f?.name ?? '',
  nameEn: f?.nameEn ?? '',
  description: f?.description ?? '',
  descriptionEn: f?.descriptionEn ?? '',
  discipline: f?.discipline ?? 'gravel',
  distanceKm: f?.distanceKm === null || !f ? '' : String(f.distanceKm),
  elevationM: f?.elevationM === null || !f ? '' : String(f.elevationM),
  startsAt: isoToEventLocal(f?.startsAt ?? null),
  capacity: f?.capacity === null || !f ? '' : String(f.capacity),
  requiresMedicalDocument: f?.requiresMedicalDocument ?? false,
  brevoListId: f?.brevoListId === null || !f ? '' : String(f.brevoListId),
  sortOrder: String(f?.sortOrder ?? nextSort),
  // A new format starts hidden: it needs a tier before it can be opened.
  status: f?.status ?? 'draft',
  registrationMode: f?.registrationMode ?? 'individual',
  teamSizeMin: f?.teamSizeMin == null ? '' : String(f.teamSizeMin),
  teamSizeMax: f?.teamSizeMax == null ? '' : String(f.teamSizeMax),
});

const toInput = (v: Values) => ({
  ...(v.slug.trim() && { slug: v.slug.trim() }),
  name: v.name,
  nameEn: v.nameEn,
  description: v.description,
  descriptionEn: v.descriptionEn,
  discipline: v.discipline,
  distanceKm: decimalOrNull(v.distanceKm),
  elevationM: intOrNull(v.elevationM),
  startsAt: v.startsAt ? eventLocalToIso(v.startsAt) : null,
  capacity: intOrNull(v.capacity),
  requiresMedicalDocument: v.requiresMedicalDocument,
  brevoListId: intOrNull(v.brevoListId),
  sortOrder: intOrNull(v.sortOrder) ?? 0,
  status: v.status,
  registrationMode: v.registrationMode,
  // Sizes only mean something for a team format (P-20); an individual format carries none.
  teamSizeMin: v.registrationMode === 'team' ? intOrNull(v.teamSizeMin) : null,
  teamSizeMax: v.registrationMode === 'team' ? intOrNull(v.teamSizeMax) : null,
});

const FIELD: Partial<Record<Field, keyof Values>> = {
  slug: 'slug',
  name: 'name',
  nameEn: 'nameEn',
  description: 'description',
  descriptionEn: 'descriptionEn',
  distanceKm: 'distanceKm',
  elevationM: 'elevationM',
  startsAt: 'startsAt',
  capacity: 'capacity',
  brevoListId: 'brevoListId',
  sortOrder: 'sortOrder',
  teamSizeMin: 'teamSizeMin',
  teamSizeMax: 'teamSizeMax',
};

/** A-01: create a format, or edit one with its pricing tiers. */
export function AdminFormatEdit() {
  const { t } = useTranslation();
  const { id } = useParams();
  const list = useAdminFormats();
  if (list.isLoading) return <p className="text-center text-ink-muted">…</p>;
  const format = id ? list.data?.formats.find((f) => f.id === id) : null;
  if (id && !format) {
    return (
      <section className="space-y-4">
        <Link to="/admin/formats">{t('admin.formats.back')}</Link>
        <p role="alert" className="text-danger">
          {t(list.isError ? 'admin.formats.error' : 'admin.formats.notFound')}
        </p>
      </section>
    );
  }
  const nextSort = Math.max(-1, ...(list.data?.formats ?? []).map((f) => f.sortOrder)) + 1;
  return (
    <section className="space-y-6">
      <Link to="/admin/formats" className="text-sm">
        {t('admin.formats.back')}
      </Link>
      <h1 className="font-display text-section-lg font-bold">
        {format ? format.name : t('admin.formats.new')}
      </h1>
      <FormatForm key={format?.id ?? 'new'} format={format ?? null} nextSort={nextSort} />
      {format && <AdminTiers format={format} />}
      {format && <DeleteFormat format={format} />}
    </section>
  );
}

function FormatForm({ format, nextSort }: { format: AdminFormat | null; nextSort: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateFormat();
  const update = useUpdateFormat(format?.id ?? '');
  const { register, handleSubmit, setError, reset, formState, watch } = useForm<Values>({
    defaultValues: toValues(format, nextSort),
  });
  const teamMode = watch('registrationMode') === 'team';
  const [pending, setPending] = useState<{ patch: UpdateFormatInput; lines: string[] } | null>(
    null,
  );
  const [notice, setNotice] = useState<'saved' | 'nothing' | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const saving = create.isPending || update.isPending;

  const showApiError = (e: unknown) => {
    const key = adminApiErrorKey(e);
    const details = (e instanceof ApiRequestError ? e.details : null) as {
      taken?: number;
    } | null;
    const message = t(`admin.error.${key}`, { taken: details?.taken });
    if (key === 'capacity_below_taken') setError('capacity', { message });
    else if (key === 'slug_taken') setError('slug', { message });
    else setApiError(message);
  };

  const save = async (patch: UpdateFormatInput) => {
    try {
      const saved = await update.mutateAsync(patch);
      reset(toValues(saved, nextSort));
      setNotice('saved');
    } catch (e) {
      showApiError(e);
    } finally {
      setPending(null);
    }
  };

  const onSubmit = async (v: Values) => {
    setNotice(null);
    setApiError(null);
    const parsed = createFormatInput.safeParse(toInput(v));
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = FIELD[issue.path[0] as Field];
        if (field) setError(field, { message: t(`admin.error.${adminIssueKey(issue)}`) });
      }
      return;
    }
    if (!format) {
      try {
        const created = await create.mutateAsync(parsed.data);
        navigate(`/admin/formats/${created.id}`, { replace: true });
      } catch (e) {
        showApiError(e);
      }
      return;
    }

    const { slug: _slug, ...next } = parsed.data;
    const patch = Object.fromEntries(
      Object.entries(next).filter(([k, val]) => format[k as keyof typeof next] !== val),
    ) as UpdateFormatInput;
    if (!Object.keys(patch).length) return setNotice('nothing');

    // Capacity and status act on the live checkout: say what changes before saving.
    const lines: string[] = [];
    const taken = format.paid + format.held;
    if (patch.capacity != null && patch.capacity < taken) {
      setError('capacity', { message: t('admin.error.capacity_below_taken', { taken }) });
      return;
    }
    if (patch.capacity !== undefined) {
      const before = format.capacity ?? t('admin.format.confirm.unlimited');
      lines.push(
        patch.capacity === null
          ? t('admin.format.confirm.capacityUnlimited', { before })
          : t('admin.format.confirm.capacity', {
              before,
              after: patch.capacity,
              taken,
              left: Math.max(0, patch.capacity - taken),
            }),
      );
    }
    if (patch.status !== undefined) {
      const vars = {
        before: t(`admin.formatStatus.${format.status}`),
        after: t(`admin.formatStatus.${patch.status}`),
      };
      const key =
        patch.status === 'open'
          ? format.tiers.some((x) => x.live)
            ? 'statusOn'
            : 'statusOnNoTier'
          : format.status === 'open'
            ? 'statusOff'
            : 'statusOther';
      lines.push(t(`admin.format.confirm.${key}`, vars));
    }
    if (lines.length) setPending({ patch, lines });
    else await save(patch);
  };

  const err = (f: keyof Values) => formState.errors[f]?.message;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card className="gap-4!">
        <h2 className="text-lg font-bold">{t('admin.format.details')}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label={t('admin.format.nameFr')} error={err('name')} {...register('name')} />
          <TextField
            label={t('admin.format.nameEn')}
            hint={t('admin.format.enHint')}
            error={err('nameEn')}
            {...register('nameEn')}
          />
          <TextAreaField
            label={t('admin.format.descriptionFr')}
            error={err('description')}
            {...register('description')}
          />
          <TextAreaField
            label={t('admin.format.descriptionEn')}
            error={err('descriptionEn')}
            {...register('descriptionEn')}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {!format && (
            <TextField
              label={t('admin.format.slug')}
              hint={t('admin.format.slugHint')}
              error={err('slug')}
              {...register('slug')}
            />
          )}
          <SelectField label={t('admin.format.discipline')} {...register('discipline')}>
            {disciplineEnum.options.map((d) => (
              <option key={d} value={d}>
                {t(`admin.discipline.${d}`)}
              </option>
            ))}
          </SelectField>
          <TextField
            label={t('admin.format.distance')}
            inputMode="decimal"
            error={err('distanceKm')}
            {...register('distanceKm')}
          />
          <TextField
            label={t('admin.format.elevation')}
            inputMode="numeric"
            error={err('elevationM')}
            {...register('elevationM')}
          />
          <TextField
            type="datetime-local"
            label={t('admin.format.startsAt')}
            error={err('startsAt')}
            {...register('startsAt')}
          />
          <TextField
            label={t('admin.format.capacity')}
            hint={
              format
                ? t('admin.format.capacityHint', { paid: format.paid, held: format.held })
                : undefined
            }
            inputMode="numeric"
            error={err('capacity')}
            {...register('capacity')}
          />
          <SelectField label={t('admin.format.registrationMode')} {...register('registrationMode')}>
            {registrationModeEnum.options.map((m) => (
              <option key={m} value={m}>
                {t(`admin.registrationMode.${m}`)}
              </option>
            ))}
          </SelectField>
          {teamMode && (
            <>
              <TextField
                label={t('admin.format.teamSizeMin')}
                hint={t('admin.format.teamSizeMinHint')}
                inputMode="numeric"
                error={err('teamSizeMin')}
                {...register('teamSizeMin')}
              />
              <TextField
                label={t('admin.format.teamSizeMax')}
                hint={t('admin.format.teamSizeMaxHint')}
                inputMode="numeric"
                error={err('teamSizeMax')}
                {...register('teamSizeMax')}
              />
            </>
          )}
          <TextField
            label={t('admin.format.brevoList')}
            hint={t('admin.format.brevoListHint')}
            inputMode="numeric"
            error={err('brevoListId')}
            {...register('brevoListId')}
          />
          <TextField
            label={t('admin.format.sortOrder')}
            inputMode="numeric"
            error={err('sortOrder')}
            {...register('sortOrder')}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <SelectField label={t('admin.format.status')} {...register('status')}>
              {formatStatus.options.map((s) => (
                <option key={s} value={s}>
                  {t(`admin.formatStatus.${s}`)}
                </option>
              ))}
            </SelectField>
            <span className="text-helper text-ink-muted">{t('admin.formatStatus.hint')}</span>
          </div>
          <div className="pt-8">
            <CheckboxField {...register('requiresMedicalDocument')}>
              {t('admin.format.medical')}
            </CheckboxField>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button type="submit" size="md" className="w-auto!" loading={saving}>
            {t(format ? 'admin.format.save' : 'admin.format.create')}
          </Button>
          {notice && (
            <span role="status" className="text-sm text-ink-muted">
              {t(`admin.format.${notice}`)}
            </span>
          )}
          {apiError && (
            <span role="alert" className="text-sm text-danger">
              {apiError}
            </span>
          )}
        </div>
      </Card>

      <ConfirmDialog
        open={pending !== null}
        title={t('admin.format.confirm.title')}
        confirmLabel={t('admin.confirm.apply')}
        cancelLabel={t('admin.confirm.cancel')}
        loading={update.isPending}
        onConfirm={() => pending && save(pending.patch)}
        onCancel={() => setPending(null)}
      >
        <div className="flex flex-col gap-3">
          <p className="m-0">{t('admin.format.confirm.live')}</p>
          <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-sm">
            {pending?.lines.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
      </ConfirmDialog>
    </form>
  );
}

function DeleteFormat({ format }: { format: AdminFormat }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const remove = useDeleteFormat(format.id);
  const [open, setOpen] = useState(false);

  if (format.registrations > 0) {
    return (
      <p className="text-sm text-ink-muted">
        {t('admin.format.deleteBlocked', { count: format.registrations })}
      </p>
    );
  }
  const confirm = async () => {
    try {
      await remove.mutateAsync(undefined);
      navigate('/admin/formats', { replace: true });
    } catch {
      setOpen(false); // Shown through remove.isError.
    }
  };
  return (
    <div className="flex items-center gap-4">
      <Button variant="secondary" size="md" className="w-auto!" onClick={() => setOpen(true)}>
        {t('admin.format.delete')}
      </Button>
      {remove.isError && (
        <span role="alert" className="text-sm text-danger">
          {t(`admin.error.${adminApiErrorKey(remove.error)}`)}
        </span>
      )}
      <ConfirmDialog
        open={open}
        title={t('admin.format.deleteConfirm.title')}
        confirmLabel={t('admin.format.delete')}
        cancelLabel={t('admin.confirm.cancel')}
        loading={remove.isPending}
        onConfirm={confirm}
        onCancel={() => setOpen(false)}
      >
        <p className="m-0">{t('admin.format.deleteConfirm.body', { name: format.name })}</p>
      </ConfirmDialog>
    </div>
  );
}
